import { ACCESS_TOKEN } from "../constants";

export const AUTHENTICATION_REQUIRED_EVENT = "palsync:authentication-required";

let accessTokenProvider = null;
let accessTokenProviderGeneration = 0;
let tokenRefreshState = null;
let lastForbiddenToken = null;

export const setAccessTokenProvider = (provider) => {
  accessTokenProvider = typeof provider === "function" ? provider : null;
  accessTokenProviderGeneration += 1;
  lastForbiddenToken = null;
};

const storeAccessToken = (token) => {
  if (typeof token !== "string" || !token.trim()) {
    throw new Error("Auth0 did not return an access token.");
  }

  localStorage.setItem(ACCESS_TOKEN, token);
  return token;
};

export const refreshStoredAccessToken = ({ force = false } = {}) => {
  if (!accessTokenProvider) {
    return Promise.reject(new Error("No access-token provider is available."));
  }

  const generation = accessTokenProviderGeneration;
  if (!tokenRefreshState || tokenRefreshState.generation !== generation) {
    const provider = accessTokenProvider;
    const options = force ? { cacheMode: "off" } : undefined;
    const promise = Promise.resolve(provider(options))
      .then((token) => {
        if (generation !== accessTokenProviderGeneration) {
          throw new Error("The access-token provider changed during refresh.");
        }
        return storeAccessToken(token);
      })
      .finally(() => {
        if (tokenRefreshState?.promise === promise) {
          tokenRefreshState = null;
        }
      });
    tokenRefreshState = { generation, promise };
  }

  return tokenRefreshState.promise;
};

const notifyAuthenticationRequired = () => {
  localStorage.removeItem(ACCESS_TOKEN);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTHENTICATION_REQUIRED_EVENT));
  }
};

const requestWithToken = (url, options, token) => {
  const headers = new Headers(options?.headers || {});
  if (typeof token === "string" && token.trim()) {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    headers.delete("Authorization");
  }

  return fetch(url, { ...options, headers });
};

export const authenticatedFetch = async (url, options = {}) => {
  let token = localStorage.getItem(ACCESS_TOKEN);
  let response = await requestWithToken(url, options, token);

  const initialStatus = response.status;
  const shouldRefreshForbidden =
    initialStatus === 403 && token !== lastForbiddenToken;
  if ((initialStatus === 401 || shouldRefreshForbidden) && accessTokenProvider) {
    try {
      token = await refreshStoredAccessToken({ force: true });
      response = await requestWithToken(url, options, token);
    } catch (_error) {
      if (initialStatus === 401) notifyAuthenticationRequired();
      if (initialStatus === 403) lastForbiddenToken = token;
      return response;
    }
  }

  if (response.status === 403) {
    lastForbiddenToken = token;
  } else if (token === lastForbiddenToken) {
    lastForbiddenToken = null;
  }

  if (response.status === 401) {
    notifyAuthenticationRequired();
  }

  return response;
};
